import React, { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { FormProvider, useForm } from 'react-hook-form';
import { useSignIn, useSignUp } from '@clerk/nextjs';
import { authIdentifierAtom, authIdentifierTypeAtom, authModeAtom, authRedirectToAtom, authStepAtom } from 'auth/atoms';
import VerificationInput from 'components/auth/components/VerificationInput';
import Button from 'components/ui/Button/Button';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import useTrackComponentMount from 'hooks/useTrackComponentMount';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import useActivateSession from 'hooks/useActivateSession';
import { LoginType } from 'auth/constants';
import { FlexJustifyCenter } from 'components/primitives/flex';
import { useUserDataMutation } from '../hooks/authFlowHooks';
import useAuthComplete from '../hooks/useAuthComplete';

type VerificationValues = {
  code: string | null;
};

const VerificationStep = () => {
  const { enqueueSnackbar } = useLazySnackbar();
  const [identifierType] = useAtom(authIdentifierTypeAtom);
  const [identifier] = useAtom(authIdentifierAtom);
  const [mode] = useAtom(authModeAtom);
  const [, setStep] = useAtom(authStepAtom);
  const methods = useForm<VerificationValues>({ defaultValues: { code: '' } });
  const { handleSubmit } = methods;
  const { signIn, isLoaded } = useSignIn();
  const { signUp } = useSignUp();
  const activateSession = useActivateSession();

  const redirectTo = useAtomValue(authRedirectToAtom); // Use useAtomValue to read the atom
  const userDataMutation = useUserDataMutation();
  const { runAnalytics } = useAuthComplete();
  const cooldownTime = 15;
  const [cooldown, setCooldown] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);

  useTrackComponentMount('show_form', {
    component_type: 'form',
    object: `verify_your_${identifierType}`,
  });
  const rudderTrack = useRudderStackTrack();

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const onSubmit = async ({ code }: VerificationValues) => {
    if (!isLoaded) return;

    rudderTrack('click', {
      component_type: 'button',
      object: 'verify',
      context: 'verify_your_email',
    });

    try {
      setIsVerifying(true);

      if (mode === 'signup') {
        const verificationAttempt = await signUp.attemptEmailAddressVerification({ code });
        if (verificationAttempt.status === 'complete') {
          await activateSession({
            session: verificationAttempt.createdSessionId,
            redirectTo,
            onAfterSuccess: async () => {
              setStep('identifier');
              const userData = await userDataMutation.mutateAsync();
              runAnalytics(userData, LoginType.EMAIL_LOGIN);
              rudderTrack('signup_success', {
                object: 'verify_your_email',
              });
            },
          });
        }
      } else {
        const signInAttempt = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
        if (signInAttempt.status === 'complete') {
          await activateSession({
            session: signInAttempt.createdSessionId,
            redirectTo,
            onAfterSuccess: async () => {
              setStep('identifier');
              const userData = await userDataMutation.mutateAsync();
              runAnalytics(userData, LoginType.EMAIL_LOGIN);
              rudderTrack('signin_success', {
                object: 'verify_your_email',
              });
            },
          });
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 5000,
      });
      console.error('Verification error:', err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerificationCode = async () => {
    rudderTrack('click', {
      component_type: 'link',
      object: 'resend_code',
      context: 'verify_your_email',
    });

    if (!isLoaded || cooldown > 0) return;

    setCooldown(cooldownTime);

    try {
      if (identifierType === 'phone') {
        await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      }
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 5000,
      });
      console.error('Resend Verfication Error:', err);
    }
  };

  const headerText = identifierType === 'phone' ? 'We sent you a text' : 'Check your email';

  const verificationText =
    identifierType === 'phone'
      ? `We sent a 6-digit code to ${identifier}. Enter it below to verify your phone number.`
      : `We sent a 6-digit code to ${identifier}. Enter it below to verify your email.`;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <VerificationInput
          headerText={headerText}
          verificationText={verificationText}
          resendCode={handleResendVerificationCode}
          resendCodeDisabled={cooldown > 0}
          resendCooldown={cooldown}
          onBackClick={() => {
            rudderTrack('click', {
              component_type: 'button',
              object: 'back_to_sign_up_complete_details_form',
              context: 'verify_your_email',
            });
            setStep('signupDetails');
          }}
        />
        <Button type="submit" text="Verify" loading={isVerifying} fullWidth />
        <FlexJustifyCenter>
          <div id="clerk-captcha" data-cl-theme="light" data-cl-size="flexible" />
        </FlexJustifyCenter>
      </form>
    </FormProvider>
  );
};

export default VerificationStep;
